import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@app/core/services';
import { Observable } from 'rxjs/Observable';
import { flatten, head } from '@app/shared/utils';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators';

const PASSWORD_MIN_LEN = 8;
const USERNAME_MAX_LEN = 150;

@Component({
  selector: 'app-auth-form',
  templateUrl: './auth-form.component.html',
  styleUrls: ['./auth-form.component.scss']
})
export class AuthFormComponent implements OnInit, OnDestroy {
  @Input() isLoginForm = true;
  @Input() isVeteranCard = false;
  @Input() showButtons = true;
  @Input() showTypes = true;
  @Output() loggedIn = new EventEmitter<null>();
  @Output() onClose = new EventEmitter<null>();
  form: FormGroup;
  isShowPassword: boolean;


  isLoading = false;

  error: string;
  apiErrors = {};

  ngUnsubscribe = new Subject();

  private validationMessages = {
    'name': {
      'required': 'Обязательное поле',
      'maxlength': `Должно быть не более ${USERNAME_MAX_LEN} символов`,
      'pattern': 'Содержит недопустимые символы',
    },
    'email': {
      'required': 'Обязательное поле',
      'email': 'Неправильный формат',
    },
    'password': {
      'required': 'Обязательное поле',
      'minlength': `Пароль должен содержать не менее ${PASSWORD_MIN_LEN} символов`,
    }
  };

  static splitFirstAndLastName(firstWithLastName: string) {
    // https://stackoverflow.com/a/25177077
    const parts = firstWithLastName.split(/[ ]+/);
    const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : parts.join(' ');
    const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
    return { firstName, lastName };
  }


  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
  ) { }

  ngOnInit() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(USERNAME_MAX_LEN)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(PASSWORD_MIN_LEN)]],
      offer: ['', ],
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  submit() {
    if (this.isLoading) return;

    this.isLoading = true;

    this.getAction().pipe(takeUntil(this.ngUnsubscribe))
      .finally(() => this.isLoading = false)
      .subscribe(this.onSuccess.bind(this), this.onError.bind(this));
  }

  getAction() {
    return this.isLoginForm ? this.login() : this.register();
  }

  private login()  {
    const { email, password } = this.form.value;
    return this.authService.login(email, password);
  }

  private register() {
    for (const fieldName of Object.keys(this.form.controls)) {
      this.form.get(fieldName).markAsTouched();
    }

    this.clearErrors();

    if (this.offerCheckbox) {
      if (!this.form.get('offer').value) {
        this.apiErrors['offer'] = 'Необходимо согласиться с публичной офертой';
        return Observable.of();
      }
    }

    if (!this.form.valid) {
      return Observable.of();
    }
    const { name, email, password } = this.form.value;
    if (this.isVeteranCard) {
      const { firstName, lastName } = AuthFormComponent.splitFirstAndLastName(name);

      return this.authService.registerVeteran({
        first_name: firstName, last_name: lastName, email
      }, password);
    } else {
      return this.authService.registerPartner({ username: name, email }, password);
    }
  }

  clearErrors() {
    this.error = null;
    this.apiErrors = {};
  }

  private onSuccess() {
    this.loggedIn.next();
  }

  private onError(error) {
    const json = error.error;
    if (error.status === 400) {
      this.error = head(json['non_field_errors']) || json.message;
      this.apiErrors = flatten(json);
    } else if (error.status === 423) {
      this.error = json && json.message || 'Ваш аккаунт заблокирован';
    }
  }

  public checkError(fieldName: string, form?: FormGroup): string {
    if (!form) form = this.form;
    const field = form.get(fieldName);

    // Do not validate login
    if (this.isLoginForm && form === this.form) return null;

    if (field.untouched) return null;

    if (this.isVeteranCard && fieldName === 'name') {
      if (this.apiErrors['first_name']) return this.apiErrors['first_name'];
      if (this.apiErrors['last_name']) return this.apiErrors['last_name'];
      if (field.value.indexOf(' ') < 0) {
        return 'Поле должно содержать ваши имя и фамилию, разделенные пробелом';
      }
    }

    if (this.apiErrors[fieldName]) return this.apiErrors[fieldName];

    if (field.valid) return null;
    for (const error of Object.keys(field.errors)) {
      return this.validationMessages[fieldName][error] || 'Некорректно заполнено поле';
    }

    return null;
  }

  changeForm(source: 'login' | 'register') {
    this.isLoginForm = source === 'login';
    this.error = null;
    this.apiErrors = {};
  }

  get offerCheckbox() {
    return !this.isLoginForm && !this.isVeteranCard;
  }

  close() {
    this.onClose.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/accounts/auth-form/auth-form.component.ts